import React from 'react';
import { TextField, Grid, Box, Typography, MenuItem } from '@mui/material';
import { Controller } from 'react-hook-form';
import useCountries from 'core/hooks/UseCountry';
import { Utils } from 'core/utils';

function PersonalInfoStep({ register, errors, watch, setValue, control, cvData }) {
    const { data: countries, isLoading: countriesLoading } = useCountries();
    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight={600}>
                Personal Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start by adding your basic information. This will be used as the foundation for your CV.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="First Name *"
                        {...register('name', { required: 'First name is required' })}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        defaultValue={cvData?.name || ''}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Last Name *"
                        {...register('last_name', { required: 'Last name is required' })}
                        error={!!errors.last_name}
                        helperText={errors.last_name?.message}
                        defaultValue={cvData?.last_name || ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="CV Title *"
                        {...register('title', { required: 'CV title is required' })}
                        error={!!errors.title}
                        helperText={errors.title?.message || 'e.g., Software Engineer, Marketing Manager'}
                        defaultValue={cvData?.title || ''}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Email *"
                        type="email"
                        {...register('email', { 
                            required: 'Email is required',
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address'
                            }
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        defaultValue={cvData?.email || ''}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Phone"
                        {...register('phone')}
                        defaultValue={cvData?.phone || ''}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="country"
                        control={control}
                        defaultValue={cvData?.country?.id || cvData?.country || ''}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                select
                                label="Country"
                                disabled={countriesLoading}
                            >
                                <MenuItem value="">Select Country</MenuItem>
                                {countries?.results?.map((country) => (
                                    <MenuItem key={country.id} value={country.id}>
                                        {Utils.getDefaultName(country)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="City"
                        {...register('city')}
                        defaultValue={cvData?.city || ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Website"
                        {...register('website')}
                        helperText="e.g., yourname.com"
                        defaultValue={cvData?.website || ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Address"
                        {...register('address')}
                        defaultValue={cvData?.address || ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="About / Description"
                        multiline
                        rows={4}
                        {...register('description')}
                        helperText="A brief summary about yourself and your professional background"
                        defaultValue={cvData?.description || ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Interests"
                        multiline
                        rows={2}
                        {...register('interests')}
                        helperText="Your hobbies and interests"
                        defaultValue={cvData?.interests || ''}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export default PersonalInfoStep;

